/**
 * Passphrase-based encryption for AliveGuardian
 *
 * Implements zero-knowledge document encryption where:
 * - Owner's key is derived from a passphrase using PBKDF2
 * - Recipient keys are random strings like AG-XXXX-XXXX-XXXX-XXXX
 * - The server never sees plaintext documents, DEKs, or key strings
 */

import { AES_ALGORITHM, AES_KEY_LENGTH, AES_KEY_USAGES } from './constants';
import { arrayBufferToBase64, base64ToArrayBuffer, getRandomBytes } from './utils';

// AES-KW (Key Wrap) algorithm for wrapping DEKs
const AES_KW_ALGORITHM = 'AES-KW';

// PBKDF2 configuration (OWASP 2024 recommendation)
export const PBKDF2_ITERATIONS = 600000;
export const PBKDF2_HASH = 'SHA-256';
export const SALT_LENGTH = 16; // bytes

/**
 * Minimum passphrase length.
 *
 * This guards against an OFFLINE attack, not an online one: a document's
 * ciphertext, IV, wrapped key and salt all live in the same database row, so a
 * single dump is a complete cracking oracle with no rate limit. PBKDF2-SHA256
 * at 600k iterations still runs on the order of 10k guesses/sec on one modern
 * GPU, which puts an 8-character human-chosen passphrase inside wordlist range
 * in hours. There is also no recovery path by design, so the cost of a weak
 * passphrase is total and permanent loss of the documents it protects.
 */
export const MIN_PASSPHRASE_LENGTH = 12;

// Recipient key format: AG-XXXX-XXXX-XXXX-XXXX
//
// 4 segments x 4 chars = 16 random characters from a 32-character alphabet, so
// exactly 16 * log2(32) = 80 bits of entropy. The "AG-" prefix is constant and
// contributes nothing. (Earlier comments here claimed ~119 and ~103 bits, which
// was wrong — do not shorten the segments on the assumption of headroom.)
const RECIPIENT_KEY_CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excludes confusing chars: 0, O, I, 1
const RECIPIENT_KEY_SEGMENT_LENGTH = 4;
const RECIPIENT_KEY_SEGMENTS = 4;

/**
 * Derive an AES-256 key from a passphrase using PBKDF2
 *
 * @param passphrase - User's passphrase
 * @param salt - Optional salt (will generate new one if not provided)
 * @returns Object containing the derived CryptoKey and salt (base64)
 */
export async function deriveKeyFromPassphrase(
  passphrase: string,
  salt?: string
): Promise<{ key: CryptoKey; salt: string }> {
  // Generate or decode salt
  let saltBytes: Uint8Array;
  if (salt) {
    saltBytes = new Uint8Array(base64ToArrayBuffer(salt));
  } else {
    saltBytes = getRandomBytes(SALT_LENGTH);
  }

  // Import passphrase as key material
  const encoder = new TextEncoder();
  const passphraseBytes = encoder.encode(passphrase);

  const keyMaterial = await crypto.subtle.importKey('raw', passphraseBytes, 'PBKDF2', false, [
    'deriveBits',
    'deriveKey',
  ]);

  // Derive AES-KW key for wrapping/unwrapping DEKs
  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    keyMaterial,
    {
      name: AES_KW_ALGORITHM,
      length: AES_KEY_LENGTH,
    },
    // NOT extractable. Only the key being *wrapped* needs to be extractable;
    // the wrapping key itself does not. Marking this one extractable let an XSS
    // call exportKey() on the long-lived passphrase-derived key and decrypt
    // every document under that salt, rather than only what was open at the time.
    false,
    ['wrapKey', 'unwrapKey']
  );

  return {
    key: derivedKey,
    salt: arrayBufferToBase64(saltBytes.buffer as ArrayBuffer),
  };
}

/**
 * Generate a recipient key string in format AG-XXXX-XXXX-XXXX-XXXX
 *
 * Uses cryptographically secure random bytes.
 * Character set excludes confusing characters (0, O, I, 1).
 * 16 random characters from a 32-char set = exactly 80 bits of entropy.
 *
 * @returns Recipient key string
 */
export function generateRecipientKeyString(): string {
  const segments: string[] = ['AG'];

  for (let s = 0; s < RECIPIENT_KEY_SEGMENTS; s++) {
    let segment = '';
    const randomBytes = getRandomBytes(RECIPIENT_KEY_SEGMENT_LENGTH);

    for (let i = 0; i < RECIPIENT_KEY_SEGMENT_LENGTH; i++) {
      const index = randomBytes[i]! % RECIPIENT_KEY_CHARSET.length;
      segment += RECIPIENT_KEY_CHARSET[index];
    }

    segments.push(segment);
  }

  return segments.join('-');
}

/**
 * Derive an AES key from a recipient key string
 *
 * The key string itself is used as the passphrase, with a fixed
 * derivation salt (since the key string has sufficient entropy).
 *
 * @param keyString - Recipient key string (AG-XXXX-XXXX-XXXX-XXXX)
 * @returns Derived CryptoKey
 */
export async function deriveKeyFromRecipientString(keyString: string): Promise<CryptoKey> {
  // Use a fixed salt for recipient keys since the key string itself has high entropy
  // This is safe because each key string is unique and random
  const fixedSalt = 'AliveGuardian-Recipient-Key-Salt';
  const encoder = new TextEncoder();
  const saltBytes = encoder.encode(fixedSalt);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(keyString),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    keyMaterial,
    {
      name: AES_KW_ALGORITHM,
      length: AES_KEY_LENGTH,
    },
    false, // not extractable — see deriveKeyFromPassphrase
    ['wrapKey', 'unwrapKey']
  );
}

/**
 * Generate a verification hash from a recipient key string.
 * This hash can be stored server-side to verify the key without
 * revealing it (zero-knowledge). Uses a different salt than the
 * key derivation to prevent any relationship between the two.
 *
 * @param keyString - Recipient key string (AG-XXXX-XXXX-XXXX-XXXX)
 * @returns Base64-encoded SHA-256 hash
 */
export async function hashRecipientKeyString(keyString: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode('AliveGuardian-Key-Verify:' + keyString);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  // Convert to base64
  const bytes = new Uint8Array(hashBuffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/**
 * Wrap (encrypt) a DEK with a passphrase-derived key using AES-KW
 *
 * @param dek - Data Encryption Key to wrap
 * @param wrappingKey - Passphrase-derived key
 * @returns Base64-encoded wrapped key
 */
export async function wrapDEKWithDerivedKey(dek: CryptoKey, wrappingKey: CryptoKey): Promise<string> {
  const wrappedKey = await crypto.subtle.wrapKey('raw', dek, wrappingKey, 'AES-KW');
  return arrayBufferToBase64(wrappedKey);
}

/**
 * Unwrap (decrypt) a DEK using a passphrase-derived key
 *
 * @param wrappedKeyBase64 - Base64-encoded wrapped key
 * @param unwrappingKey - Passphrase-derived key
 * @returns Unwrapped DEK
 */
export async function unwrapDEKWithDerivedKey(
  wrappedKeyBase64: string,
  unwrappingKey: CryptoKey
): Promise<CryptoKey> {
  const wrappedKey = base64ToArrayBuffer(wrappedKeyBase64);

  return crypto.subtle.unwrapKey(
    'raw',
    wrappedKey,
    unwrappingKey,
    'AES-KW',
    { name: AES_ALGORITHM },
    true,
    AES_KEY_USAGES
  );
}

/**
 * Validate a passphrase meets minimum requirements
 *
 * @param passphrase - Passphrase to validate
 * @returns Object with isValid boolean and optional error message
 */
export function validatePassphrase(passphrase: string): { isValid: boolean; error?: string } {
  if (!passphrase || passphrase.length < MIN_PASSPHRASE_LENGTH) {
    return {
      isValid: false,
      error: `Passphrase must be at least ${MIN_PASSPHRASE_LENGTH} characters`,
    };
  }

  return { isValid: true };
}

/**
 * Calculate passphrase strength (0-4 scale)
 *
 * 0 = very weak
 * 1 = weak
 * 2 = fair
 * 3 = good
 * 4 = strong
 *
 * @param passphrase - Passphrase to evaluate
 * @returns Strength score (0-4)
 */
export function calculatePassphraseStrength(passphrase: string): number {
  if (!passphrase) return 0;

  let score = 0;

  // Length checks
  if (passphrase.length >= 8) score++;
  if (passphrase.length >= 12) score++;
  if (passphrase.length >= 16) score++;

  // Character variety
  const hasLower = /[a-z]/.test(passphrase);
  const hasUpper = /[A-Z]/.test(passphrase);
  const hasNumber = /[0-9]/.test(passphrase);
  const hasSpecial = /[^a-zA-Z0-9]/.test(passphrase);

  const varietyCount = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  if (varietyCount >= 3) score++;

  return Math.min(score, 4);
}

/**
 * Validate a recipient key string format
 *
 * @param keyString - Key string to validate
 * @returns True if valid format
 */
export function isValidRecipientKeyString(keyString: string): boolean {
  // Format: AG-XXXX-XXXX-XXXX-XXXX
  // Character class mirrors RECIPIENT_KEY_CHARSET exactly. The old [A-Z2-9]
  // admitted I and O, which the generator deliberately excludes, so a user who
  // transcribed O for 0 got a confusing decrypt failure instead of a format error.
  const pattern = /^AG-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
  return pattern.test(keyString);
}
